import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import {
  CREATE_ORDER_MUTATION,
  UPDATE_CART_MUTATION,
} from '@/hooks/use-new-order-actions-queries';
import { DEFAULT_ORDER_DOMAINS } from '@/contexts/new-order-context';
import {
  getBusinessUUIDsFromChatVariantPacksPriceEditMapKey,
  getBusinessUUIDsFromOfflineOrderContextCacheKey,
} from '@/utils/offline-mode';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  NewOrder_CreateOrderMutation,
  NewOrder_CreateOrderMutationVariables,
  NewOrder_UpdateCartMutation,
  NewOrder_UpdateCartMutationVariables,
  OfflineReplay_GetDraftOrdersQuery,
  OfflineReplay_GetDraftOrdersQueryVariables,
  SalesRep_UpdateGrossProfitPercentMutation,
  SalesRep_UpdateGrossProfitPercentMutationVariables,
  SalesRep_UpdatePriceMutation,
  SalesRep_UpdatePriceMutationVariables,
  SalesRepUpdateVariantPackGrossProfitInput,
  SalesRepUpdateVariantPackInput,
} from '@/types';
import {
  OfflineOrderContext,
  OfflineOrderContextAdapter,
  OfflineVariantPackPriceEditAdapter,
} from '@/adapters/offline-mode';
import { ORDER_CONTEXT_QUERY } from '@/contexts/new-order-context-queries';
import { OrderValidationErrorCode, SalesRepOverrideMode } from '@/enums';
import {
  SalesRepGrossProfitValidationErrorCode,
  SalesRepPriceValidationErrorCode,
} from '@platform/errors';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import {
  UPDATE_SALES_REP_GROSS_PROFIT_PERCENT_MUTATION,
  UPDATE_SALES_REP_PRICE_MUTATION,
} from '@/hooks/use-price-editing';
import { useCallback, useRef } from 'react';
import { useDownloadVariantPackInfo } from './use-download-variant-pack-info';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useOfflineModeStyledToasts } from './use-offline-mode-styled-toasts';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export const GET_DRAFT_ORDERS_QUERY = gql`
  query OfflineReplay_GetDraftOrders(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $unplacedOrderStatuses: [OrderStatus!]!
    $orderDomains: [OrderDomain!]!
  ) {
    getOrders(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      filters: {
        statuses: $unplacedOrderStatuses
        order_domains: $orderDomains
      }
    ) {
      order {
        restaurant_uuid
        uuid
      }
    }
  }
`;

const getOfflineDraftOrdersToSync = async (supplierUUID: string) => {
  const allOfflineDraftOrders =
    await OfflineOrderContextAdapter.getAllCacheKeys();
  const restaurantsWithDraftOrders = allOfflineDraftOrders.map(
    (key) =>
      getBusinessUUIDsFromOfflineOrderContextCacheKey(key).restaurantUUID,
  );

  const orderContextMap: {
    [restaurantUUID: string]: OfflineOrderContext;
  } = {};

  for (let restaurantUUID of restaurantsWithDraftOrders) {
    try {
      const orderContext = await OfflineOrderContextAdapter.getInstance(
        restaurantUUID,
        supplierUUID,
      ).fetch();

      if (
        orderContext &&
        orderContext.newOrderContext &&
        orderContext.newOrderContext.getOrders &&
        orderContext.newOrderContext.getOrders.length > 0 &&
        !orderContext.synced
      ) {
        orderContextMap[restaurantUUID] = orderContext;
      }
    } catch (error) {
      // Capture the error but otherwise ignore it and continue
      // getting draft orders for other restaurants in the loop
      captureException(error);
    }
  }

  return orderContextMap;
};

const useReplayDraftOrders = () => {
  const { showErrorToast, showSuccessToast } = useOfflineModeStyledToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID,
  } = useUserContext();

  const getDraftOrders = useLazyQuery<
    OfflineReplay_GetDraftOrdersQuery,
    OfflineReplay_GetDraftOrdersQueryVariables
  >(GET_DRAFT_ORDERS_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const [updateCart] = useMutation<
    NewOrder_UpdateCartMutation,
    NewOrder_UpdateCartMutationVariables
  >(UPDATE_CART_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [OrderValidationErrorCode.ORDER_ALREADY_PLACED],
    },
  });

  const [createOrder] = useMutation<
    NewOrder_CreateOrderMutation,
    NewOrder_CreateOrderMutationVariables
  >(CREATE_ORDER_MUTATION);

  const replayDraftOrders = useCallback(async () => {
    if (!supplierUUID) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    const restaurantToOfflineDraftOrder =
      await getOfflineDraftOrdersToSync(supplierUUID);

    if (!restaurantToOfflineDraftOrder) {
      return;
    }

    const restaurantUUIDs = Object.keys(restaurantToOfflineDraftOrder);
    if (restaurantUUIDs.length !== 0) {
      const draftOrdersAcrossRestaurants: OfflineReplay_GetDraftOrdersQuery[] =
        [];

      for (let restaurantUUID of restaurantUUIDs) {
        const { data: draftOrders } = await getDraftOrders({
          variables: {
            orderDomains: DEFAULT_ORDER_DOMAINS,
            restaurantUUID,
            supplierUUID,
            unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
          },
        });

        draftOrdersAcrossRestaurants.push(draftOrders);
      }

      const restaurantUUIDToOrderUUIDMap = draftOrdersAcrossRestaurants.reduce(
        (acc, order) => {
          const restaurantUUID = order.getOrders[0]?.order?.restaurant_uuid;
          const orderUUID = order.getOrders[0]?.order?.uuid;
          if (restaurantUUID && orderUUID) {
            acc[restaurantUUID] = orderUUID;
          }
          return acc;
        },
        {} as { [restaurantUUID: string]: string },
      );

      for (let restaurantUUID of restaurantUUIDs) {
        try {
          const existingDraftOrderUUID =
            restaurantUUIDToOrderUUIDMap[restaurantUUID];
          const offlineDraftOrder =
            restaurantToOfflineDraftOrder[restaurantUUID].newOrderContext
              .getOrders[0].order!;

          if (existingDraftOrderUUID) {
            await updateCart({
              awaitRefetchQueries: false,
              refetchQueries:
                restaurantUUID === selectedRestaurantUUID
                  ? [getOperationName(ORDER_CONTEXT_QUERY) as string]
                  : [],
              variables: {
                orderUUID: existingDraftOrderUUID,
                updatedItems: offlineDraftOrder.orders_items.map(
                  (orderItem) => ({
                    cart_details_at_time_of_order: {
                      quantity:
                        orderItem.order_item_prices?.pack_quantity_at_order ||
                        0,
                    },
                    variant_pack_id: orderItem.variants_pack.uuid,
                  }),
                ),
              },
            });
          } else {
            const { data: createdOrder } = await createOrder({
              variables: {
                deliveryDate:
                  offlineDraftOrder.restaurant_desired_delivery_time,
                fulfillmentType: offlineDraftOrder.fulfillment_type,
                restaurantUUID: restaurantUUID,
                supplierUUID: supplierUUID,
                timeslotEnd: offlineDraftOrder.fulfillment_timeslot_end,
                timeslotStart: offlineDraftOrder.fulfillment_timeslot_start,
              },
            });

            const createdOrderUUID = createdOrder?.createOrder.order?.uuid;

            if (createdOrderUUID) {
              await updateCart({
                awaitRefetchQueries: false,
                refetchQueries:
                  restaurantUUID === selectedRestaurantUUID
                    ? [getOperationName(ORDER_CONTEXT_QUERY) as string]
                    : [],
                variables: {
                  orderUUID: createdOrderUUID,
                  updatedItems: offlineDraftOrder.orders_items.map(
                    (orderItem) => ({
                      cart_details_at_time_of_order: {
                        quantity:
                          orderItem.order_item_prices?.pack_quantity_at_order ||
                          0,
                      },
                      variant_pack_id: orderItem.variants_pack.uuid,
                    }),
                  ),
                },
              });
            }
          }

          await OfflineOrderContextAdapter.getInstance(
            restaurantUUID,
            supplierUUID,
          ).save({
            ...restaurantToOfflineDraftOrder[restaurantUUID],
            synced: true,
          });
          successCount += 1;
        } catch (error) {
          errorCount += 1;
          captureException(error);
        }
      }

      if (successCount > 0) {
        showSuccessToast(
          i18n.t('number_orders_synced', {
            count: successCount,
          }),
        );
      }

      if (errorCount) {
        showErrorToast(
          i18n.t('number_orders_failed_sync', {
            count: successCount,
          }),
        );
      }
    }
  }, [
    createOrder,
    getDraftOrders,
    selectedRestaurantUUID,
    showErrorToast,
    showSuccessToast,
    supplierUUID,
    updateCart,
  ]);

  return { replayDraftOrders };
};

const useReplayPriceEdits = () => {
  const { showErrorToast, showSuccessToast } = useOfflineModeStyledToasts();
  const { downloadVariantPackInfo } = useDownloadVariantPackInfo();
  const [updateSalesRepGrossProfitPercentage] = useMutation<
    SalesRep_UpdateGrossProfitPercentMutation,
    SalesRep_UpdateGrossProfitPercentMutationVariables
  >(UPDATE_SALES_REP_GROSS_PROFIT_PERCENT_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [
        SalesRepGrossProfitValidationErrorCode.SALES_REP_GROSS_PROFIT_TOO_HIGH,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_LOW,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_HIGH,
      ],
    },
  });

  const [updateSalesRepPrice] = useMutation<
    SalesRep_UpdatePriceMutation,
    SalesRep_UpdatePriceMutationVariables
  >(UPDATE_SALES_REP_PRICE_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_LOW,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_HIGH,
      ],
    },
  });

  const replayPriceEdits = useCallback(async () => {
    const offlineVariantPackPriceEditAdapter =
      new OfflineVariantPackPriceEditAdapter();
    const offlinePriceEdits = await offlineVariantPackPriceEditAdapter.fetch();

    if (!offlinePriceEdits) {
      return;
    }

    for (let chatKey in offlinePriceEdits) {
      const { restaurantUUID, supplierUUID } =
        getBusinessUUIDsFromChatVariantPacksPriceEditMapKey(chatKey);
      const grossProfitOverrides: SalesRepUpdateVariantPackGrossProfitInput[] =
        [];
      const priceOverrides: SalesRepUpdateVariantPackInput[] = [];

      const variantPacksPriceEditMap = offlinePriceEdits[chatKey];
      for (let variantPackId in variantPacksPriceEditMap) {
        const priceEditInfo = variantPacksPriceEditMap[variantPackId];
        switch (priceEditInfo.type) {
          case SalesRepOverrideMode.GROSS_PROFIT:
            grossProfitOverrides.push({
              gross_profit_in_percentage: priceEditInfo.value?.toString(),
              variant_pack_id: variantPackId,
            });
            break;
          case SalesRepOverrideMode.PRICE:
            priceOverrides.push({
              sales_rep_price_in_micros: priceEditInfo.value,
              variant_pack_id: variantPackId,
            });
            break;
          default:
            // `SalesRepOverrideMode.DISABLED` state maybe
            break;
        }
      }

      try {
        if (grossProfitOverrides.length > 0) {
          // GP% overrides should be rounded before they're added to the offline adapter
          await updateSalesRepGrossProfitPercentage({
            variables: {
              restaurantUUID,
              supplierUUID,
              variantPacks: grossProfitOverrides,
            },
          });
          showSuccessToast(
            i18n.t('gross_profit_sync', {
              count: grossProfitOverrides.length,
            }),
          );
        }
      } catch (error) {
        showErrorToast(
          i18n.t('gross_profit_sync_failed', {
            count: grossProfitOverrides.length,
          }),
        );
        captureException(error);
      }

      try {
        if (priceOverrides.length > 0) {
          // Price overrides should be rounded before they're added to the offline adapter
          await updateSalesRepPrice({
            variables: {
              restaurantUUID,
              supplierUUID,
              variantPacks: priceOverrides,
            },
          });
          showSuccessToast(
            i18n.t('price_override_sync', {
              count: priceOverrides.length,
            }),
          );
        }
      } catch (error) {
        showErrorToast(
          i18n.t('price_override_sync_failed', {
            count: priceOverrides.length,
          }),
        );
        captureException(error);
      }

      // Re-download the variant pack info now that price edits have been synced
      downloadVariantPackInfo(restaurantUUID, supplierUUID);
    }

    // Regardless of success/failure, remove the price edits so they aren't synced again.
    // We are intentionally discarding failed edits, as they may no longer be valid
    // (e.g. setting a Price Override lower than a newly ingested Sales Code)
    offlineVariantPackPriceEditAdapter.remove();
  }, [
    downloadVariantPackInfo,
    showErrorToast,
    showSuccessToast,
    updateSalesRepGrossProfitPercentage,
    updateSalesRepPrice,
  ]);

  return { replayPriceEdits };
};

export const useReplayOfflineOperations = () => {
  const { showErrorToast } = useToasts();

  const { replayDraftOrders } = useReplayDraftOrders();
  const { replayPriceEdits } = useReplayPriceEdits();
  const isReplaying = useRef(false);

  const replayOfflineOperations = useCallback(async () => {
    if (isReplaying.current) {
      return;
    }

    isReplaying.current = true;

    try {
      await replayPriceEdits();
      await replayDraftOrders();
    } catch (error) {
      // We handle data syncing errors in the individual updates themselves
      // This is a catch all in case anything slips through, we don't expect it to happen
      showErrorToast();
      captureException(error);
    } finally {
      isReplaying.current = false;
    }
  }, [replayDraftOrders, replayPriceEdits, showErrorToast]);

  return { replayOfflineOperations };
};
